<template>
  <!-- 巡检任务：负责状态筛选、分页加载和选中任务通知。 -->
  <aside class="patrol-task-pane" @scroll.passive="handleScroll">
    <section class="patrol-task-card">
      <div class="patrol-task-header">
        <h2>巡检任务</h2>
        <select v-model="selectedDeviceId" :disabled="devicesLoading || !devices.length" aria-label="选择设备"
          @change="changeDevice">
          <option v-if="!devices.length" value="">{{ devicesLoading ? '设备加载中…' : '暂无设备' }}</option>
          <option v-for="device in devices" :key="deviceId(device)" :value="String(deviceId(device))">
            {{ deviceLabel(device) }}
          </option>
        </select>
      </div>
      <div class="task-count">{{ tasks.length }} / {{ total }} 条</div>
      <!-- 0 未检验，1 检验完成。 -->
      <div class="patrol-task-tabs" role="tablist" aria-label="巡检任务状态">
        <button type="button" :class="{ active: status === 0 }" @click="changeStatus(0)">未检验</button>
        <button type="button" :class="{ active: status === 1 }" @click="changeStatus(1)">检验完成</button>
      </div>
      <button v-for="task in tasks" :key="task.id" type="button" class="patrol-task-item"
        :class="{ active: isSelected(task) }" @click="selectTask(task)">
        <span class="task-check" :class="{ completed: taskStatus(task).code === 1 }">✓</span>
        <span class="task-info">
          <strong>{{ task.code }}</strong>
          <span>{{ task.processName || '未设置工序' }}</span>
          <span>{{ task.orderCode || '未关联生产订单' }}</span>
        </span>
        <span class="task-status" :class="taskStatus(task).badgeClass">{{ taskStatus(task).name || '待检验' }}</span>
      </button>
      <div class="patrol-task-state">
        <ASpin v-if="loading" size="small" />
        <span v-else-if="finished">没有更多数据</span>
        <span v-else>下滑加载更多</span>
      </div>
    </section>
  </aside>
</template>

<script>
import {
  mapGetters,
  mapMutations,
} from 'vuex'
import { deviceInfoGetList , inspectionPatrolTaskList } from '@api'
export default {
  name: "InspectionTasks",
  data() {
    return {
      tasks: [],
      devices: [],
      selectedDeviceId: '',
      devicesLoading: false,
      page: 1,
      limit: 7,
      total: 0,
      loading: false,
      finished: false,
      status: 0,
      requestId: 0,
    };
  },
  mounted() {
    this.loadDevices();
  },
  computed: {
    ...mapGetters(["getRefresh", "getWorkOrder"]),
  },
  watch: {
    getRefresh() {
      this.refreshTasks();
    },
  },
  methods: {
    ...mapMutations(["setWorkOrder"]),
    async loadDevices() {
      this.devicesLoading = true;
      try {
        const response = await deviceInfoGetList ();
        const payload = response && response.data !== undefined ? response.data : response;
        const devices = Array.isArray(payload)
          ? payload
          : Array.isArray(payload && payload.data)
            ? payload.data
            : Array.isArray(payload && payload.records)
              ? payload.records
              : [];
        this.devices = devices.filter(device => this.deviceId(device) !== undefined && this.deviceId(device) !== null);
        if (this.devices.length) {
          this.selectedDeviceId = String(this.deviceId(this.devices[0]));
          await this.refreshTasks();
        } else {
          this.finished = true;
        }
      } catch (error) {
        this.$message.error("设备列表加载失败，请稍后重试。");
      } finally {
        this.devicesLoading = false;
      }
    },
    deviceId(device) {
      return device && (device.id !== undefined ? device.id : device.deviceInfoId);
    },
    deviceLabel(device) {
      return device && (device.name || device.deviceName || device.deviceInfoName || device.code) || `设备 ${this.deviceId(device)}`;
    },
    changeDevice() {
      this.refreshTasks();
    },
    // 巡检列表触底后，成功加载当前页才递增页码。
    async loadTasks(force = false) {
      if (!force && (this.loading || this.finished)) return;
      if (!this.selectedDeviceId) return;

      const requestId = ++this.requestId;
      const requestStatus = this.status;
      const requestDeviceId = this.selectedDeviceId;
      this.loading = true;
      try {
        const response = await inspectionPatrolTaskList({
          page: this.page,
          limit: this.limit,
          status: requestStatus,
          deviceInfoId: requestDeviceId,
        });
        // axios 响应拦截器已返回业务响应体，因此直接读取 data 分页对象。
        const pageData = response.data || response;
        const records = Array.isArray(pageData.data) ? pageData.data : [];

        // 标签切换时，忽略此前请求晚到的响应。
        if (requestId !== this.requestId) return;

        this.tasks = this.tasks.concat(records);
        this.total = Number(pageData.total) || 0;
        this.page += 1;
        this.finished = !records.length || this.tasks.length >= this.total;
      } catch (error) {
        if (requestId === this.requestId) {
          this.$message.error("巡检任务加载失败，请稍后重试。");
        }
      } finally {
        if (requestId === this.requestId) {
          this.loading = false;
        }
      }
    },
    changeStatus(status) {
      if (this.status === status) return;

      this.status = status;
      this.refreshTasks();
    },
    // 外部刷新或状态切换都必须从第 1 页重新取数，不能沿用触底后的 finished 状态。
    refreshTasks() {
      this.requestId += 1;
      this.tasks = [];
      this.page = 1;
      this.total = 0;
      this.finished = false;
      this.setWorkOrder({});
      if (!this.selectedDeviceId) {
        this.finished = true;
        this.loading = false;
        return Promise.resolve();
      }
      return this.loadTasks(true);
    },
    handleScroll(event) {
      const { scrollTop, clientHeight, scrollHeight } = event.target;
      if (scrollTop + clientHeight >= scrollHeight - 24) {
        this.loadTasks();
      }
    },
    taskStatus(task) {
      return task.status || {};
    },
    // 优先使用 Vuex 当前任务 id；扫码与列表的任务 id 不一致时以任务编码兜底。
    isSelected(task) {
      const selectedTask = this.getWorkOrder;
      return String(selectedTask.id) === String(task.id)
    },
    // 写入 Vuex 后，列表按当前任务 id 或任务编码判断唯一高亮项。
    selectTask(task) {
      this.setWorkOrder(task);
    },
  },
};
</script>

<style lang="less" scoped>
.patrol-task-pane {
  box-sizing: border-box;
  width: 360px;
  min-width: 360px;
  max-width: 360px;
  height: 100%;
  max-height: 100%;
  min-height: 0;
  padding: 20px 14px 110px;
  // 始终预留纵向滚动条空间，切换标签时内容宽度不会因滚动条出现而变化。
  overflow-y: scroll;
  scrollbar-gutter: stable;
  background: #edf2f7;
  border-right: 1px solid #d5e0ea;
}

.patrol-task-card {
  box-sizing: border-box;
  min-height: 100%;
  padding: 16px;
  background: #fff;
  border: 1px solid #d9e4ee;
  border-radius: 16px;
}

.patrol-task-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding-bottom: 8px;
  border-bottom: 1px solid #dce5ee;
}

.patrol-task-header h2 {
  margin: 0;
  font-size: 22px;
}

.patrol-task-header select {
  box-sizing: border-box;
  width: 145px;
  min-width: 0;
  height: 34px;
  padding: 0 9px;
  color: #315672;
  font-size: 13px;
  background: #f7fafc;
  border: 1px solid #cbd9e5;
  border-radius: 7px;
}

.task-count,
.patrol-task-state {
  color: #537291;
  font-size: 13px;
}

.task-count {
  padding-top: 8px;
  text-align: right;
}

.patrol-task-tabs {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  padding: 12px 0 2px;
}

.patrol-task-tabs button {
  height: 32px;
  color: #59728d;
  font-size: 13px;
  cursor: pointer;
  background: #f2f6fa;
  border: 1px solid #d8e3ed;
  border-radius: 7px;
}

.patrol-task-tabs button.active {
  color: #176ed0;
  font-weight: 600;
  background: #eaf4ff;
  border-color: #76aee6;
}

.patrol-task-item {
  display: grid;
  grid-template-columns: 36px minmax(0, 1fr) auto;
  gap: 10px;
  align-items: start;
  width: 100%;
  padding: 13px 12px;
  margin-top: 10px;
  color: #192d45;
  text-align: left;
  cursor: pointer;
  background: #fff;
  border: 1px solid #d6e2ed;
  border-radius: 12px;
}

.patrol-task-item:hover,
.patrol-task-item.active {
  background: #f2f8ff;
  border-color: #6fa6de;
}

.task-check {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  color: #63809d;
  font-size: 22px;
  background: #edf4fb;
  border-radius: 10px;
}

.task-check.completed {
  color: #116cc0;
  background: #e5f1fc;
}

.task-info {
  display: grid;
  min-width: 0;
  gap: 2px;
}

.task-info strong,
.task-info span {
  overflow: hidden;
  font-size: 14px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.task-info strong {
  color: #111;
}

.task-info span:last-child {
  color: #31465b;
}

.task-status {
  padding: 4px 8px;
  color: #9b5b17;
  font-size: 12px;
  white-space: nowrap;
  background: #fff3e2;
  border-radius: 7px;
}

.task-status.success {
  color: #167b4d;
  background: #e3f6ec;
}

.patrol-task-state {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 28px;
  padding-top: 10px;
}

@media (max-width: 900px) {
  .patrol-task-pane {
    width: auto;
    min-width: 0;
    max-width: none;
    height: auto;
    min-height: 0;
    max-height: 310px;
    padding: 14px;
    border-right: 0;
    border-bottom: 1px solid #d5e0ea;
  }
}
</style>
